import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { ORAKLE_WEATHER_WEBSITE, ORAKLE_WEATHER_LOGO_URL } from '../../constants/colors';
import 'bootstrap/dist/css/bootstrap.css';
import '../../pages/admin/AdminAuth.css';

const MFA = () => {
    const [mfaCode, setMfaCode] = useState('');
    const [localError, setLocalError] = useState(null);
    const { verifyMfa, resetMfa, isLoading, error, clearError, isAuthenticated, mfaRequired, sessionId } = useAdminAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        clearError();

        // Check if we have a session ID
        const stateSessionId = location.state?.session_id;
        if (!sessionId && !stateSessionId) {
            navigate('/login');
        }
    }, [clearError, sessionId, location.state, navigate]);

    useEffect(() => {
        if (isAuthenticated && !mfaRequired) {
            navigate('/welcome');
        }
    }, [isAuthenticated, mfaRequired, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError(null);
        clearError();

        const currentSessionId = sessionId || location.state?.session_id;
        if (!currentSessionId) {
            setLocalError('Session invalide. Veuillez vous reconnecter.');
            return;
        }

        if (mfaCode.length < 6) {
            setLocalError('Le code MFA doit contenir au moins 6 caractères');
            return;
        }

        try {
            await verifyMfa(mfaCode);
        } catch (err) {
            setLocalError(err.message || 'Une erreur est survenue');
        }
    };

    const handleResetMfa = async () => {
        setLocalError(null);
        clearError();

        const currentSessionId = sessionId || location.state?.session_id;
        if (!currentSessionId) {
            setLocalError('Session invalide. Veuillez vous reconnecter.');
            return;
        }

        try {
            await resetMfa();
            setMfaCode('');
        } catch (err) {
            setLocalError(err.message || 'Une erreur est survenue');
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    const currentSessionId = sessionId || location.state?.session_id;
    if (!currentSessionId && !isLoading) {
        return null; // useEffect will redirect
    }

    return (
        <div className="admin-auth-container">
            <div className="admin-auth-card">
                <div className="admin-auth-header">
                    <div className="auth-page__logo">
                        <a href={ORAKLE_WEATHER_WEBSITE} target="_blank" rel="noopener noreferrer">
                            <img src={ORAKLE_WEATHER_LOGO_URL} alt="Orakle Weather" />
                        </a>
                    </div>
                    <h1>Vérification MFA</h1>
                    <p>Entrez le code de vérification envoyé à votre appareil</p>
                </div>

                <form onSubmit={handleSubmit} className="admin-auth-form">
                    {error && (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    )}

                    {localError && (
                        <div className="alert alert-danger" role="alert">
                            {localError}
                        </div>
                    )}

                    <div className="mb-3">
                        <label htmlFor="mfa_code" className="form-label">
                            Code de vérification
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="mfa_code"
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                            placeholder="123456"
                            maxLength={8}
                            required
                            disabled={isLoading}
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary w-100 mb-3"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Vérification en cours...' : 'Vérifier le code'}
                    </button>

                    <button
                        type="button"
                        className="btn btn-secondary w-100 mb-3"
                        onClick={handleResetMfa}
                        disabled={isLoading}
                    >
                        Réinitialiser le code MFA
                    </button>

                    <button
                        type="button"
                        className="btn btn-link w-100"
                        onClick={handleBackToLogin}
                        disabled={isLoading}
                    >
                        Retour à la connexion
                    </button>
                </form>
            </div>
        </div>
    );
};

export default MFA;

